import { useQuery } from '@apollo/client';
import {
  Box,
  Thead,
  Th,
  Tr,
  Table,
  Td,
  Tbody,
  Flex,
  Badge,
  TableContainer,
  Spinner,
  Button,
  Icon,
  Tooltip,
  Text,
  Switch,
  FormControl,
  FormLabel,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@chakra-ui/react';
import { SortAscending, SortDescending, Equals, ArrowsClockwise } from '@phosphor-icons/react';
import { RouteComponentProps } from '@reach/router';
import { useState, useEffect } from 'react';

import { formatUTCToLocal } from '../../../utils';
import {
  GET_ACTIVITY_POOL,
  GET_REACTIVATION_POOL,
  InternalActivityPoolQuery,
  InternalReactivationPoolQuery,
  ActivitySortField,
} from './query';
import { Pagination } from '../../../components/Pagination';
import Layout from '../../../components/Layout';
import { FilterSelectPill } from '../../../components/FilterPill/Select';
import { FilterDateRangePill } from '../../../components/FilterPill/DateRange';
import { AccessPointOverviewTab } from './AccessPointOverviewTab';

interface ActivityPoolPageProps extends RouteComponentProps {}

type PoolType = 'activity' | 'reactivation' | '';

const getUrlParams = () => new URLSearchParams(window.location.search);

const formatTriggeredBy = (triggeredBy: string, employeeName?: string | null): string => {
  if (triggeredBy === 'APP') return 'APP';
  if (employeeName) return `Mitarbeiter (${employeeName})`;
  return 'Mitarbeiter';
};

const POLLING_INTERVAL = 10000;

const ActivityPoolTab: React.FC = () => {
  const limit = 50;

  const [autoRefresh, setAutoRefresh] = useState(true);

  const params = getUrlParams();
  const [poolType, setPoolType] = useState<PoolType>((params.get('pool') as PoolType) || '');
  const [offset, setOffset] = useState(parseInt(params.get('offset') || '0', 10));
  const [triggeredByFilter, setTriggeredByFilter] = useState<string[]>(params.get('triggeredBy') ? [params.get('triggeredBy')!] : []);
  const [sortBy, setSortBy] = useState<ActivitySortField>((params.get('sortBy') as ActivitySortField) || 'createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>((params.get('sortOrder') as 'asc' | 'desc') || 'desc');

  const [pausedSinceRange, setPausedSinceRange] = useState({
    startDate: params.get('pausedSinceFrom') || '',
    endDate: params.get('pausedSinceTo') || '',
  });
  const [reactivatedSinceRange, setReactivatedSinceRange] = useState({
    startDate: params.get('reactivatedSinceFrom') || '',
    endDate: params.get('reactivatedSinceTo') || '',
  });

  useEffect(() => {
    const params = new URLSearchParams();

    if (poolType) params.set('pool', poolType);
    if (offset > 0) params.set('offset', offset.toString());
    if (triggeredByFilter.length > 0) params.set('triggeredBy', triggeredByFilter[0]);
    if (sortBy !== 'createdAt') params.set('sortBy', sortBy);
    if (sortOrder !== 'desc') params.set('sortOrder', sortOrder);

    if (pausedSinceRange.startDate) params.set('pausedSinceFrom', pausedSinceRange.startDate);
    if (pausedSinceRange.endDate) params.set('pausedSinceTo', pausedSinceRange.endDate);
    if (reactivatedSinceRange.startDate) params.set('reactivatedSinceFrom', reactivatedSinceRange.startDate);
    if (reactivatedSinceRange.endDate) params.set('reactivatedSinceTo', reactivatedSinceRange.endDate);

    const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
    window.history.replaceState({}, '', newUrl);
  }, [poolType, offset, triggeredByFilter, sortBy, sortOrder, pausedSinceRange, reactivatedSinceRange]);

  const {
    data: activityData,
    loading: activityLoading,
    refetch: activityRefetch,
  } = useQuery<InternalActivityPoolQuery>(GET_ACTIVITY_POOL, {
    variables: {
      limit,
      offset,
      sortBy,
      sortOrder,
      triggeredBy: triggeredByFilter.length > 0 ? triggeredByFilter[0] : undefined,
      pausedSinceFrom: pausedSinceRange.startDate || undefined,
      pausedSinceTo: pausedSinceRange.endDate || undefined,
      reactivatedSinceFrom: reactivatedSinceRange.startDate || undefined,
      reactivatedSinceTo: reactivatedSinceRange.endDate || undefined,
    },
    skip: poolType === 'reactivation',
    pollInterval: autoRefresh && poolType !== 'reactivation' ? POLLING_INTERVAL : 0,
  });

  const {
    data: reactivationData,
    loading: reactivationLoading,
    refetch: reactivationRefetch,
  } = useQuery<InternalReactivationPoolQuery>(GET_REACTIVATION_POOL, {
    variables: {
      limit,
      offset,
      sortBy,
      sortOrder,
      triggeredBy: triggeredByFilter.length > 0 ? triggeredByFilter[0] : undefined,
      pausedSinceFrom: pausedSinceRange.startDate || undefined,
      pausedSinceTo: pausedSinceRange.endDate || undefined,
    },
    skip: poolType !== 'reactivation',
    pollInterval: autoRefresh && poolType === 'reactivation' ? POLLING_INTERVAL : 0,
  });

  const loading = poolType === 'reactivation' ? reactivationLoading : activityLoading;
  const refetch = poolType === 'reactivation' ? reactivationRefetch : activityRefetch;

  const handleLoadMore = (page: number) => {
    setOffset((page - 1) * limit);
  };

  const handleSort = (field: ActivitySortField) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setOffset(0);
  };

  const renderSortIcon = (field: ActivitySortField) => {
    if (sortBy !== field) {
      return <Equals size={16} style={{ display: 'inline', marginLeft: '0.25rem', opacity: 0.4, verticalAlign: 'middle' }} />;
    }
    return sortOrder === 'asc' ? (
      <SortAscending size={16} style={{ display: 'inline', marginLeft: '0.25rem', verticalAlign: 'middle' }} />
    ) : (
      <SortDescending size={16} style={{ display: 'inline', marginLeft: '0.25rem', verticalAlign: 'middle' }} />
    );
  };

  const totalCount =
    poolType === 'reactivation'
      ? reactivationData?.internalReactivationPool.totalCount || 0
      : activityData?.internalActivityPool.totalCount || 0;

  return (
    <>
      {/* Filters */}
      <Flex justifyContent="flex-start" alignItems="center" gap="2" mb="2" flexWrap="wrap">
        <FilterSelectPill
          label="Pool"
          options={[
            { label: 'Activity Pool', value: 'activity' },
            { label: 'Reactivation Pool', value: 'reactivation' },
          ]}
          initialValues={poolType ? [poolType] : []}
          setValues={(values) => {
            const newPool = (values.length > 0 ? values[values.length - 1] : '') as PoolType;
            setPoolType(newPool);
            setOffset(0);
            if (newPool === 'reactivation') {
              setReactivatedSinceRange({ startDate: '', endDate: '' });
            }
          }}
          clear={() => {
            setPoolType('');
            setOffset(0);
          }}
        />
        <FilterSelectPill
          label="Ausgelöst durch"
          options={[
            { label: 'APP', value: 'APP' },
            { label: 'Mitarbeiter', value: 'EMPLOYEE' },
          ]}
          initialValues={triggeredByFilter}
          setValues={(values) => {
            setTriggeredByFilter(values);
            setOffset(0);
          }}
          clear={() => {
            setTriggeredByFilter([]);
            setOffset(0);
          }}
        />
        <FilterDateRangePill
          label="Pausiert seit"
          initialValues={pausedSinceRange}
          setValues={(values) => {
            setPausedSinceRange(values);
            setOffset(0);
          }}
          clear={() => {
            setPausedSinceRange({ startDate: '', endDate: '' });
            setOffset(0);
          }}
        />
        {poolType !== 'reactivation' && (
          <FilterDateRangePill
            label="Reaktiviert seit"
            initialValues={reactivatedSinceRange}
            setValues={(values) => {
              setReactivatedSinceRange(values);
              setOffset(0);
            }}
            clear={() => {
              setReactivatedSinceRange({ startDate: '', endDate: '' });
              setOffset(0);
            }}
          />
        )}
      </Flex>

      {/* Refresh Controls */}
      <Flex justifyContent="space-between" alignItems="center" gap="4" mb="2">
        <Text fontSize="sm" color="gray.500">
          {totalCount} Einträge gefunden
        </Text>
        <Flex alignItems="center" gap="4">
          <FormControl display="flex" alignItems="center" width="auto">
            <Switch id="auto-refresh" isChecked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} colorScheme="blue" />
            <FormLabel htmlFor="auto-refresh" mb="0" fontSize="sm" ml="2">
              Auto-Refresh (10s)
            </FormLabel>
          </FormControl>
          <Tooltip label="Tabelle aktualisieren" hasArrow>
            <Button size="sm" variant="outline" leftIcon={<ArrowsClockwise size={16} />} onClick={() => refetch()} isLoading={loading}>
              Aktualisieren
            </Button>
          </Tooltip>
        </Flex>
      </Flex>

      {/* Table */}
      <Box>
        <TableContainer>
          <Table variant="striped" size="sm">
            <Thead>
              {poolType !== 'reactivation' ? (
                <Tr>
                  <Th>Typ</Th>
                  <Th>Name</Th>
                  <Th>PLZ</Th>
                  <Th>Stadt</Th>
                  <Th>UPS AP seit</Th>
                  <Th cursor="pointer" userSelect="none" onClick={() => handleSort('createdAt')}>
                    <Flex alignItems="center" gap="2">
                      <span>Aktivität</span>
                      {renderSortIcon('createdAt')}
                    </Flex>
                  </Th>
                  <Th>Kommentar</Th>
                </Tr>
              ) : (
                <Tr>
                  <Th>Typ</Th>
                  <Th>Name</Th>
                  <Th>PLZ</Th>
                  <Th>Stadt</Th>
                  <Th>UPS AP seit</Th>
                  <Th cursor="pointer" userSelect="none" onClick={() => handleSort('pausedSince')}>
                    <Flex alignItems="center" gap="2">
                      <span>Aktivität</span>
                      {renderSortIcon('pausedSince')}
                    </Flex>
                  </Th>
                  <Th>Wird aktiv am</Th>
                  <Th>Kommentar</Th>
                </Tr>
              )}
            </Thead>
            {loading ? null : (
              <Tbody>
                {poolType !== 'reactivation' &&
                  activityData?.internalActivityPool.entries.map((entry) => (
                    <Tr key={entry.id}>
                      <Td>
                        <Badge colorScheme={entry.action === 'PAUSED' ? 'red' : 'green'} fontSize="xs">
                          {entry.action === 'PAUSED' ? 'Pausierung' : 'Aktivierung'}
                        </Badge>
                      </Td>
                      <Td>{entry.locationName || '-'}</Td>
                      <Td>{entry.postalCode || '-'}</Td>
                      <Td>{entry.city || '-'}</Td>
                      <Td>{formatUTCToLocal(entry.upsApSince) || '-'}</Td>
                      <Td>
                        <Text fontSize="sm">
                          {entry.action === 'PAUSED' ? 'Pausiert' : 'Reaktiviert'} am: {formatUTCToLocal(entry.createdAt)}
                        </Text>
                        <Text fontSize="sm">
                          {entry.action === 'PAUSED' ? 'Pausiert' : 'Reaktiviert'} durch:{' '}
                          {formatTriggeredBy(entry.triggeredBy, entry.employeeName)}
                        </Text>
                      </Td>
                      <Td>{entry.comment || '-'}</Td>
                    </Tr>
                  ))}
                {poolType === 'reactivation' &&
                  reactivationData?.internalReactivationPool.entries.map((entry) => (
                    <Tr key={entry.id}>
                      <Td>
                        <Badge colorScheme="red" fontSize="xs">
                          Pausierung
                        </Badge>
                      </Td>
                      <Td>{entry.locationName || '-'}</Td>
                      <Td>{entry.postalCode || '-'}</Td>
                      <Td>{entry.city || '-'}</Td>
                      <Td>{formatUTCToLocal(entry.upsApSince) || '-'}</Td>
                      <Td>
                        <Text fontSize="sm">Pausiert am: {formatUTCToLocal(entry.pausedSince)}</Text>
                        <Text fontSize="sm">Pausiert durch: {formatTriggeredBy(entry.triggeredBy, entry.employeeName)}</Text>
                      </Td>
                      <Td>{formatUTCToLocal(entry.deactivatedUntil) || '-'}</Td>
                      <Td>{entry.comment || '-'}</Td>
                    </Tr>
                  ))}
              </Tbody>
            )}
          </Table>
        </TableContainer>
        {loading ? (
          <Flex width="100%" height="100%" justifyContent="center" alignItems="center" p="12">
            <Spinner />
          </Flex>
        ) : null}
        <Box>
          <Pagination totalAmount={totalCount} amountPerPage={limit} currentPage={offset / limit + 1} handleOnClick={handleLoadMore} />
        </Box>
      </Box>
    </>
  );
};

export const AccessPointsActivityPool: React.FC<ActivityPoolPageProps> = () => {
  return (
    <Layout>
      <Tabs variant="line" colorScheme="brand">
        <TabList mb="4">
          <Tab>Activity Pool</Tab>
          <Tab>Access Point Übersicht</Tab>
        </TabList>
        <TabPanels>
          <TabPanel p="0">
            <ActivityPoolTab />
          </TabPanel>
          <TabPanel p="0">
            <AccessPointOverviewTab />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Layout>
  );
};
